/* eslint-disable no-undef */
import { useEffect, useRef, useState } from 'react'
import { useGoogleMap } from '@react-google-maps/api'
import { useNavigate } from 'react-router-dom'
import { scrollToTop } from 'utils/utils'

// Buttons shown in the popup of the route line, e.g. "See route" and "Book Now!"
export type TRouteAction = {
  label: string
  path: string
  primary?: boolean
}

type Props = {
  url?: string
  visible?: boolean
  actions?: TRouteAction[]
}

const ICON_SIZE = 24

const iconSize = (feature: google.maps.Data.Feature) => ICON_SIZE * (feature.getProperty('iconScale') ?? 1)

// Popup styles live in styles/app.css (.route-popup-*)
const popupTitle = (feature: google.maps.Data.Feature) => {
  const title = document.createElement('div')
  title.className = 'route-popup-title'
  title.textContent = feature.getProperty('name')
  return title
}

const popupContent = (feature: google.maps.Data.Feature, actions: TRouteAction[], navigate: (path: string) => void) => {
  const content = document.createElement('div')
  content.className = 'route-popup-desc'
  content.textContent = feature.getProperty('description') ?? ''

  const isLine = feature.getGeometry()?.getType() === 'LineString'
  if (isLine && actions.length) {
    const buttons = document.createElement('div')
    buttons.className = 'route-popup-actions'

    actions.forEach(({ label, path, primary }) => {
      const link = document.createElement('a')
      link.className = primary ? 'route-popup-btn route-popup-btn-primary' : 'route-popup-btn'
      link.href = path
      link.textContent = label
      link.addEventListener('click', (event) => {
        event.preventDefault()
        scrollToTop()
        navigate(path)
      })
      buttons.appendChild(link)
    })

    content.appendChild(buttons)
  }

  return content
}

// One popup per map, shared by all routes, so opening one closes the other
const infoWindows = new WeakMap<google.maps.Map, { infoWindow: google.maps.InfoWindow; openedBy?: google.maps.Data }>()

const getInfoWindow = (map: google.maps.Map) => {
  if (!infoWindows.has(map)) infoWindows.set(map, { infoWindow: new google.maps.InfoWindow() })
  return infoWindows.get(map)
}

// Draws one route (line + points) from public/routes, see Constants/ROUTES.ts.
// The route is loaded once, `visible` only shows or hides it.
const RouteLayer = ({ url, visible = true, actions = [] }: Props) => {
  const map = useGoogleMap()
  const navigate = useNavigate()
  const [layer, setLayer] = useState<google.maps.Data>()

  // Read on click, so new actions or a new navigate don't reload the route
  const actionsRef = useRef(actions)
  actionsRef.current = actions
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    if (!map || !url) return

    const routeLayer = new google.maps.Data()
    const popup = getInfoWindow(map)

    routeLayer.loadGeoJson(url)
    routeLayer.setStyle((feature) => {
      const icon = feature.getProperty('icon')
      const size = iconSize(feature)

      return {
        strokeColor: feature.getProperty('stroke'),
        strokeWeight: feature.getProperty('strokeWidth'),
        strokeOpacity: 1,
        icon: icon && {
          url: icon,
          scaledSize: new google.maps.Size(size, size),
          anchor: new google.maps.Point(size / 2, size / 2)
        }
      }
    })

    const clickListener = routeLayer.addListener('click', (event: google.maps.Data.MouseEvent) => {
      const geometry = event.feature.getGeometry()
      const isPoint = geometry?.getType() === 'Point'

      // headerContent puts the title next to the close button, it's newer than our @types/google.maps
      popup.infoWindow.setOptions({
        headerContent: popupTitle(event.feature),
        content: popupContent(event.feature, actionsRef.current, (path) => navigateRef.current(path)),
        position: isPoint ? (geometry as google.maps.Data.Point).get() : event.latLng,
        pixelOffset: new google.maps.Size(0, isPoint ? -iconSize(event.feature) / 2 : 0)
      } as google.maps.InfoWindowOptions)
      // Don't move focus into the popup, it put a focus ring on the first button for mouse clicks
      popup.infoWindow.open({ map, shouldFocus: false })
      popup.openedBy = routeLayer
    })

    setLayer(routeLayer)

    return () => {
      clickListener.remove()
      if (popup.openedBy === routeLayer) popup.infoWindow.close()
      routeLayer.setMap(null)
    }
  }, [map, url])

  useEffect(() => {
    if (!map || !layer) return

    layer.setMap(visible ? map : null)

    // Only close the popup if it belongs to the route being hidden
    const popup = getInfoWindow(map)
    if (!visible && popup.openedBy === layer) popup.infoWindow.close()
  }, [map, layer, visible])

  return null
}

export default RouteLayer
